/**
 * "Run with inputs" bench (DecisionRules Test Bench equivalent).
 * Renders the auto-generated input form for the agent's declared input
 * model and starts a session with the entered values.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play as RunIcon } from 'lucide-react';
import { Button, Textarea } from '@coordio/ui';
import { InlineErrorPanel, LoadingSkeleton } from '@coordio/ui/layout';
import type { AgentDefinitionDto } from '../../types/agent';
import type { CreateSessionRequest } from '../../types/session';
import { sessionService } from '../../services/sessionService';
import { useNotificationStore } from '../../store/notificationStore';
import { ParameterForm } from '../sessions/ParameterForm';

interface AgentRunBenchProps {
  agent: AgentDefinitionDto;
}

export const AgentRunBench = ({ agent }: AgentRunBenchProps) => {
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const defaultValues = useMemo(() => {
    const defaults: Record<string, any> = {};
    agent.parameters?.forEach((param) => {
      if (!param.isSecret && param.defaultValue) {
        if (param.type === 'boolean') {
          defaults[param.key] = param.defaultValue === 'true';
        } else if (param.type === 'number' || param.type === 'int' || param.type === 'integer') {
          defaults[param.key] = Number(param.defaultValue);
        } else {
          defaults[param.key] = param.defaultValue;
        }
      }
    });
    return defaults;
  }, [agent.parameters]);

  const [values, setValues] = useState<Record<string, any>>(defaultValues);
  const [initialMessage, setInitialMessage] = useState('');
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const missingRequired = (agent.parameters ?? [])
    .filter((p) => p.isRequired && (!p.isSecret || !p.hasSecretConfigured))
    .filter((p) => {
      const value = values[p.key];
      return value === undefined || value === null || value === '';
    })
    .map((p) => p.label || p.key);

  const handleRun = async () => {
    setError(null);
    if (missingRequired.length > 0) {
      setError(`Missing required inputs: ${missingRequired.join(', ')}`);
      return;
    }

    setStarting(true);
    try {
      const nonSecretValues = Object.fromEntries(
        Object.entries(values).filter(
          ([key]) => !agent.parameters?.some((p) => p.key === key && p.isSecret),
        ),
      );
      const request: CreateSessionRequest = {
        agentDefinitionId: agent.id,
        parameters: Object.keys(nonSecretValues).length > 0 ? nonSecretValues : undefined,
        initialMessage: initialMessage.trim() || undefined,
      };
      const session = await sessionService.createSession(request);
      addNotification('Session started', 'success');
      navigate(`/sessions/${session.id}`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to start the session');
    } finally {
      setStarting(false);
    }
  };

  return (
    <div>
      {error && (
        <div className="mb-6">
          <InlineErrorPanel message={error} action={<Button type="button" variant="ghost" size="sm" onClick={() => setError(null)}>Dismiss</Button>} />
        </div>
      )}

      <ParameterForm parameters={agent.parameters ?? []} values={values} onChange={setValues} />

      <div className="flex flex-col gap-1 mt-3">
        <label className="text-sm font-medium">Initial message (optional)</label>
        <Textarea
          className="w-full"
          value={initialMessage}
          onChange={(e) => setInitialMessage(e.target.value)}
          rows={3}
        />
      </div>

      <div className="flex justify-end mt-6">
        <Button
          type="button"
          variant="default"
          onClick={handleRun}
          disabled={starting || missingRequired.length > 0}
        >
          {starting ? <LoadingSkeleton className="h-[18px] w-[18px] mr-1" /> : <RunIcon size={18} className="mr-1" />}
          {starting ? 'Starting...' : 'Run with inputs'}
        </Button>
      </div>
    </div>
  );
};
